import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  RefreshCcw,
  EyeOff,
  Eye,
  Layers,
  Sparkles,
  Search,
} from "lucide-react";
import { toast } from "sonner";

const STYLE_BADGE = {
  ART_DECO: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  CLASSIC: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  MODERN: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  NEOCLASSIC: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

/**
 * 2D Style × Category visibility matrix.
 *
 * Admin can quickly archive (sim_type, style, category_slug) triples so the
 * category is removed from the user-facing picker / catalog when the matching
 * style is selected. Also exposes the global toggle `auto_hide_empty_categories`
 * which auto-hides any category with 0 photos for the active style.
 *
 * Backend endpoints:
 *   • GET    /api/admin/catalog/style-visibility
 *   • PATCH  /api/admin/catalog/style-visibility   {sim_type,style,category_slug,is_hidden}
 *   • GET    /api/admin/settings/catalog
 *   • PATCH  /api/admin/settings/catalog            {auto_hide_empty_categories}
 */
export default function StyleCategoryMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subType, setSubType] = useState("INTERIOR");
  const [search, setSearch] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [savingSetting, setSavingSetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/admin/catalog/style-visibility");
      setData(resp);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Eroare la încărcarea matricii",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const styles = useMemo(
    () => (data?.styles && data.styles.length ? data.styles : ["ART_DECO", "CLASSIC", "MODERN", "NEOCLASSIC"]),
    [data],
  );

  const categories = useMemo(() => {
    const list = data?.categories?.[subType] || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      `${c.name} ${c.slug}`.toLowerCase().includes(q),
    );
  }, [data, subType, search]);

  const toggleCell = async (catSlug, style, currentHidden) => {
    const key = `${subType}-${style}-${catSlug}`;
    setSavingKey(key);
    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, matrix: { ...prev.matrix } };
      next.matrix[subType] = { ...prev.matrix[subType] };
      next.matrix[subType][catSlug] = {
        ...prev.matrix[subType][catSlug],
        [style]: !currentHidden,
      };
      return next;
    });
    try {
      await api.patch("/admin/catalog/style-visibility", {
        sim_type: subType,
        style,
        category_slug: catSlug,
        is_hidden: !currentHidden,
      });
      toast.success(
        !currentHidden
          ? `Arhivat «${catSlug}» pentru ${style.replace("_", " ")}`
          : `Reactivat «${catSlug}» pentru ${style.replace("_", " ")}`,
      );
    } catch (e) {
      // Revert on error
      setData((prev) => {
        if (!prev) return prev;
        const next = { ...prev, matrix: { ...prev.matrix } };
        next.matrix[subType] = { ...prev.matrix[subType] };
        next.matrix[subType][catSlug] = {
          ...prev.matrix[subType][catSlug],
          [style]: currentHidden,
        };
        return next;
      });
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Nu am putut actualiza arhivarea",
      );
    } finally {
      setSavingKey(null);
    }
  };

  const toggleAutoHide = async (next) => {
    setSavingSetting(true);
    try {
      const { data: resp } = await api.patch("/admin/settings/catalog", {
        auto_hide_empty_categories: next,
      });
      setData((prev) => (prev ? { ...prev, settings: resp } : prev));
      toast.success(
        next
          ? "Categoriile fără produse vor fi auto-ascunse"
          : "Categoriile fără produse rămân vizibile cu badge gol",
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Nu am putut salva setarea",
      );
    } finally {
      setSavingSetting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="py-20 flex items-center justify-center text-zinc-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Se încarcă matricea stiluri × categorii...
      </div>
    );
  }

  const autoHide = !!data?.settings?.auto_hide_empty_categories;
  const totalArchived = data
    ? Object.values(data.matrix || {}).reduce((sum, byType) => {
        for (const slug of Object.keys(byType)) {
          for (const st of Object.keys(byType[slug])) {
            if (byType[slug][st]) sum += 1;
          }
        }
        return sum;
      }, 0)
    : 0;

  return (
    <div className="space-y-6" data-testid="style-category-matrix-tab">
      {/* Header */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
              <Layers className="text-orange-400" size={20} />
              Stiluri & Categorii — vizibilitate per stil
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-3xl">
              Bifează căsuța pentru a <strong>arhiva</strong> o categorie pentru un anumit stil — astfel categoria nu va mai apărea în selectorul utilizatorilor când aleg stilul respectiv, iar AI-ul nu va mai primi instrucțiuni să o folosească. Setarea este complementară comutatorului global de vizibilitate din tab-ul &bdquo;Catalog FDP&rdquo;.
            </p>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-md border border-white/10 hover:bg-white/5 text-zinc-300 shrink-0"
            data-testid="matrix-refresh"
            title="Reîncarcă"
          >
            <RefreshCcw size={14} />
          </button>
        </div>
      </div>

      {/* Global setting toggle */}
      <div
        className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-4 flex items-start justify-between gap-4 flex-wrap"
        data-testid="auto-hide-empty-row"
      >
        <div className="flex items-start gap-3 min-w-0">
          <Sparkles className="text-orange-400 shrink-0 mt-0.5" size={18} />
          <div className="min-w-0">
            <div className="text-white font-bold text-sm">
              Ascunde automat categoriile goale
            </div>
            <p className="text-xs text-zinc-400 mt-1 max-w-2xl leading-relaxed">
              Când e <strong>ON</strong>, utilizatorii NU vor vedea în selector categoriile care au <strong>0 poze</strong> în stilul ales. Când e <strong>OFF</strong>, categoriile goale apar gri cu badge &bdquo;NEDISPONIBIL&rdquo; și buton &bdquo;Solicit profil&rdquo; — permițând clienților să ceară produse noi.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {savingSetting && (
            <Loader2 className="text-orange-400 animate-spin" size={14} />
          )}
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            {autoHide ? "ON" : "OFF"}
          </span>
          <Switch
            checked={autoHide}
            disabled={savingSetting}
            onCheckedChange={toggleAutoHide}
            data-testid="auto-hide-empty-switch"
          />
        </div>
      </div>

      {/* Sub-type tabs + search + summary */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-white/10 bg-zinc-900/40">
          <button
            type="button"
            onClick={() => setSubType("INTERIOR")}
            className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition ${
              subType === "INTERIOR"
                ? "bg-orange-500 text-white"
                : "text-zinc-400 hover:text-white"
            }`}
            data-testid="matrix-tab-interior"
          >
            Interior · {data?.categories?.INTERIOR?.length || 0}
          </button>
          <button
            type="button"
            onClick={() => setSubType("EXTERIOR")}
            className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition ${
              subType === "EXTERIOR"
                ? "bg-orange-500 text-white"
                : "text-zinc-400 hover:text-white"
            }`}
            data-testid="matrix-tab-exterior"
          >
            Exterior · {data?.categories?.EXTERIOR?.length || 0}
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-zinc-500">
            Arhivări active:{" "}
            <span className="text-orange-400 font-bold">{totalArchived}</span>
          </div>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută categorie..."
              className="w-56 pl-8 pr-2 py-1.5 rounded-md border border-white/10 bg-zinc-900/60 text-zinc-200 text-xs focus:outline-none focus:border-orange-500/50"
              data-testid="matrix-search"
            />
          </div>
        </div>
      </div>

      {/* Matrix */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 overflow-x-auto">
        <table className="w-full text-sm" data-testid="matrix-table">
          <thead>
            <tr className="border-b border-white/10 bg-zinc-950/50">
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-[28%] min-w-[220px]">
                Categorie
              </th>
              {styles.map((st) => (
                <th
                  key={st}
                  className="px-3 py-3 text-center text-[10px] uppercase tracking-[0.18em] font-semibold"
                >
                  <span
                    className={`inline-block px-2 py-0.5 rounded border ${STYLE_BADGE[st]}`}
                  >
                    {st.replace("_", " ")}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td
                  colSpan={styles.length + 1}
                  className="text-center py-12 text-zinc-500 text-sm"
                >
                  Nicio categorie potrivită filtrului.
                </td>
              </tr>
            ) : (
              categories.map((c) => {
                const row = data?.matrix?.[subType]?.[c.slug] || {};
                return (
                  <tr
                    key={c.slug}
                    className="border-b border-white/5 hover:bg-zinc-900/40"
                    data-testid={`matrix-row-${c.slug}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-bold text-white text-sm">
                        {c.name}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                        {c.slug}
                      </div>
                    </td>
                    {styles.map((st) => {
                      const hidden = !!row[st];
                      const photosForStyle =
                        (c.photos_by_style && c.photos_by_style[st]) || 0;
                      const key = `${subType}-${st}-${c.slug}`;
                      const saving = savingKey === key;
                      return (
                        <td
                          key={st}
                          className="px-3 py-3 text-center align-middle"
                        >
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => toggleCell(c.slug, st, hidden)}
                            className={`relative inline-flex flex-col items-center gap-1 px-2.5 py-1.5 rounded-md border transition w-full max-w-[110px] ${
                              hidden
                                ? "border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/15"
                                : photosForStyle === 0
                                ? "border-zinc-700/40 bg-zinc-900/40 text-zinc-500 hover:bg-zinc-900/60"
                                : "border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/15"
                            } ${saving ? "opacity-50 cursor-wait" : ""}`}
                            data-testid={`matrix-cell-${subType}-${st}-${c.slug}`}
                            title={
                              hidden
                                ? `Arhivat. Click pentru a reactiva pentru ${st.replace("_", " ")}.`
                                : `Vizibil. Click pentru a arhiva pentru ${st.replace("_", " ")}.`
                            }
                          >
                            {saving ? (
                              <Loader2
                                size={12}
                                className="animate-spin"
                              />
                            ) : hidden ? (
                              <EyeOff size={12} />
                            ) : (
                              <Eye size={12} />
                            )}
                            <span className="text-[10px] uppercase tracking-wider font-bold">
                              {hidden ? "Arhivat" : "Vizibil"}
                            </span>
                            <span className="text-[9px] text-zinc-500">
                              {photosForStyle} poze
                            </span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10" />
          Vizibil (cu poze)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm border border-zinc-700/40 bg-zinc-900/40" />
          Vizibil (0 poze)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm border border-rose-500/40 bg-rose-500/10" />
          Arhivat (ascuns pentru utilizatori)
        </span>
      </div>
    </div>
  );
}
